import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Flame, TrendingUp, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import StreakRaiseDetailModal from '@/components/StreakRaiseDetailModal';
import SwipeHint, { useSwipeHint } from '@/components/SwipeHint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  king:       { emoji: '\u{1F451}', label: 'King' },
  aristocrat: { emoji: '\u{1F3C6}', label: 'Aristocrat' },
  achiever:   { emoji: '\u{1F396}\uFE0F', label: 'Achiever' },
};

const FREQUENCY_PER_YEAR = {
  Weekly: 52,
  Monthly: 12,
  Quarterly: 4,
  'Semi-Annually': 2,
  Annually: 1,
};

const fmtUSD = (n, opts = {}) => {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const digits = opts.digits ?? (abs >= 100 ? 0 : abs >= 10 ? 1 : 2);
  return `$${n.toFixed(digits)}`;
};

/**
 * Build a downloadable .ics calendar file for a single dividend
 * payment. Pure client-side — no backend round-trip. Returns a Blob
 * URL the caller can plug into an `<a download>` click.
 */
const buildIcsBlobUrl = ({ ticker, name, amount, payDate, shares }) => {
  // payDate comes as "Jul 29, 2026" or ISO "2026-07-29".
  let d = null;
  if (payDate && typeof payDate === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(payDate)) {
      d = new Date(payDate + 'T00:00:00Z');
    } else {
      const parsed = Date.parse(payDate);
      if (!Number.isNaN(parsed)) d = new Date(parsed);
    }
  }
  if (!d || Number.isNaN(d.getTime())) return null;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const dateStr = `${yyyy}${mm}${dd}`;
  // Fold long lines per RFC 5545. Most calendar apps handle short
  // single-event ICS just fine without folding, so we keep this
  // short on purpose.
  const totalNote = (shares && amount)
    ? ` — ~${fmtUSD(shares * amount, { digits: 2 })} on ${shares} shares`
    : '';
  const summary = `${ticker} dividend · $${Number(amount).toFixed(4)}/share${totalNote}`;
  const description = `${name || ticker}: dividend pay date.\\nLast declared amount: $${Number(amount).toFixed(4)} per share${totalNote}.\\nGenerated by DiviDrip.`;
  const uid = `divdrip-${ticker}-${dateStr}@dividrip`;
  // DTSTAMP uses current UTC time
  const now = new Date();
  const stamp = `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, '0')}${String(now.getUTCDate()).padStart(2, '0')}T${String(now.getUTCHours()).padStart(2, '0')}${String(now.getUTCMinutes()).padStart(2, '0')}${String(now.getUTCSeconds()).padStart(2, '0')}Z`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//DiviDrip//Dividend Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${dateStr}`,
    `DTEND;VALUE=DATE:${dateStr}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  return { url: URL.createObjectURL(blob), filename: `${ticker}-dividend-${dateStr}.ics` };
};

/**
 * Compute annualized income bump for a single owned streak hit:
 *   shares × (last_amount − prev_amount) × payouts_per_year
 * Returns null when any input is missing.
 */
const computeYearlyBump = ({ shares, lastAmount, prevAmount, frequency }) => {
  if (!shares || !lastAmount || !prevAmount) return null;
  const ppy = FREQUENCY_PER_YEAR[frequency];
  if (!ppy) return null;
  const delta = Number(lastAmount) - Number(prevAmount);
  if (!(delta > 0)) return null;
  return Number(shares) * delta * ppy;
};

/**
 * StreakHighlightRow — passive-engagement nudge that surfaces King /
 * Aristocrat / Achiever stocks whose most-recent dividend was a
 * positive raise within the last N days. Same data the per-stock
 * flame badge reads, just promoted to a one-row "what's new" highlight.
 *
 * Per-chip enrichments:
 *   • Owned chips render emerald (vs amber) and show a `+$X/yr`
 *     micro-badge — annualized income bump for the user's actual lot.
 *   • "Add to Calendar" .ics download link surfaces next_payment_date
 *     in the user's calendar app.
 *
 * Header rollup:
 *   • "You hold N (TICKERS) · +$X/yr" emerald pill renders only when
 *     N > 0 (silent when nothing to celebrate).
 *
 * Renders nothing when zero streak raises in the window.
 */
export default function StreakHighlightRow({ days = 60, onStockClick, ownedTickers = [], tierFilter = null }) {
  const [items, setItems] = useState(null);
  const swipeRef = useRef(null);
  const [swipeHint, updateSwipeHint] = useSwipeHint(swipeRef, [tierFilter, items]);
  const [ownedShares, setOwnedShares] = useState({});
  // Active raise click — opens the slim Recent Raise Detail modal.
  // We pass the full payload so the modal's math has everything it
  // needs without an extra round-trip.
  const [activeRaise, setActiveRaise] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/screener/streaks-this-week`, { params: { days } });
        if (cancelled) return;
        const raw = Array.isArray(res.data?.items) ? res.data.items : [];
        // Optional client-side narrow to a single tier (used on
        // KingsChallenge / AristocratsChallenge / AchieversChallenge
        // pages so each only shows their own tier's recent raises).
        const filtered = tierFilter
          ? raw.filter((it) => (it.dividend_tier || '').toLowerCase() === String(tierFilter).toLowerCase())
          : raw;
        setItems(filtered);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [days, tierFilter]);

  // Pull `{ticker: shares}` so we can show real $/yr math on owned
  // chips. Only attempted when the parent says the user owns at least
  // one of the surfaced tickers — saves a hit for guests/empty users.
  useEffect(() => {
    let cancelled = false;
    if (!ownedTickers || ownedTickers.length === 0) {
      setOwnedShares({});
      return undefined;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/portfolio/owned-shares`, { withCredentials: true });
        if (!cancelled && res.data && typeof res.data === 'object') setOwnedShares(res.data);
      } catch {
        // Non-fatal — pill simply skips the +$/yr rollup
      }
    })();
    return () => { cancelled = true; };
  }, [ownedTickers]);

  if (!items || items.length === 0) return null;

  const ownedSet = new Set((ownedTickers || []).map((t) => String(t).toUpperCase()));
  const ownedHits = items.filter((s) => ownedSet.has(String(s.ticker).toUpperCase()));
  const ownedHitTickers = ownedHits.map((s) => s.ticker);

  // Sum +$/yr across owned hits for the header rollup.
  let ownedYearlyBumpTotal = 0;
  let ownedYearlyBumpKnown = false;
  ownedHits.forEach((s) => {
    const sh = ownedShares[s.ticker] || ownedShares[String(s.ticker).toUpperCase()];
    const bump = computeYearlyBump({
      shares: sh,
      lastAmount: s.recent_div_last_amount,
      prevAmount: s.recent_div_prev_amount,
      frequency: s.frequency,
    });
    if (bump != null) {
      ownedYearlyBumpTotal += bump;
      ownedYearlyBumpKnown = true;
    }
  });

  return (
    <div
      className="mb-3 rounded-lg border border-amber-400/40 bg-gradient-to-r from-amber-50/70 via-orange-50/40 to-transparent dark:from-amber-950/30 dark:via-orange-950/20 dark:to-transparent px-3 py-2 shadow-[0_4px_12px_-2px_rgba(15,23,42,0.14),0_2px_4px_-1px_rgba(15,23,42,0.06)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_2px_8px_-2px_rgba(0,0,0,0.6)]"
      data-testid="streak-highlight-row"
    >
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Flame className="w-3.5 h-3.5 text-orange-500 shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200">
          Streaks Extended
        </span>
        <span className="text-[11px] text-amber-700 dark:text-amber-300/80">
          {tierFilter
            ? `${String(tierFilter).charAt(0).toUpperCase() + String(tierFilter).slice(1)}s who just raised - last ${days} days`
            : `Kings / Aristocrats / Achievers who just raised - last ${days} days`}
        </span>
        {ownedHits.length > 0 && (
          <span
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold ring-1 ring-emerald-500/30"
            data-testid="streak-you-hold-pill"
            title={`Your portfolio holds ${ownedHits.length} of these — your income just got bumped on: ${ownedHitTickers.join(', ')}`}
          >
            You hold {ownedHits.length}
            {ownedHits.length <= 3 && (
              <span className="font-mono opacity-90">({ownedHitTickers.join(', ')})</span>
            )}
            {ownedYearlyBumpKnown && ownedYearlyBumpTotal > 0 && (
              <span
                className="font-mono"
                data-testid="streak-you-hold-yearly-bump"
                title="Annualized income bump from these raises across your held lots: shares × per-payment $ change × payments/year"
              >
                · +{fmtUSD(ownedYearlyBumpTotal, { digits: 2 })}/yr
              </span>
            )}
          </span>
        )}
        <InfoTip
          content="Companies with 10+ years of consecutive dividend raises that just declared another raise — keeping their streak alive. Click a chip to open the stock detail."
          maxWidth={260}
        />
      </div>

      <div ref={swipeRef} onScroll={updateSwipeHint} className="flex items-stretch gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {items.map((s) => {
          const meta = TIER_META[(s.dividend_tier || '').toLowerCase()] || { emoji: '', label: '' };
          const pct = Number(s.recent_div_change_pct);
          const pctStr = `+${pct.toFixed(1)}%`;
          const streak = s.dividend_streak_years
            ? ` · ${s.dividend_streak_years}y streak`
            : '';
          // Next payment preview: amount comes from the just-raised
          // dividend (recent_div_last_amount), date from next_payment_date
          // which Yahoo returns as "Jul 29, 2026" — we slice to "Jul 29"
          // to keep the chip compact.
          const nextAmt = s.recent_div_last_amount != null
            ? `~$${Number(s.recent_div_last_amount).toFixed(2)}`
            : null;
          const nextDate = (() => {
            const d = s.next_payment_date;
            if (!d || typeof d !== 'string') return null;
            const m = d.match(/^([A-Za-z]+\s+\d+),/);
            return m ? m[1] : d;
          })();
          const sublineParts = [];
          if (nextAmt) sublineParts.push(`next ${nextAmt}`);
          if (nextDate) sublineParts.push(nextDate);
          const subline = sublineParts.join(' · ');

          const isOwned = ownedSet.has(String(s.ticker).toUpperCase());
          const userShares = isOwned ? (ownedShares[s.ticker] || ownedShares[String(s.ticker).toUpperCase()]) : null;
          const yearlyBump = isOwned
            ? computeYearlyBump({
                shares: userShares,
                lastAmount: s.recent_div_last_amount,
                prevAmount: s.recent_div_prev_amount,
                frequency: s.frequency,
              })
            : null;

          // Build .ics handler — only when we have both an amount AND a
          // parsable next_payment_date.
          const ics = (s.recent_div_last_amount != null && s.next_payment_date)
            ? buildIcsBlobUrl({
                ticker: s.ticker,
                name: s.name,
                amount: s.recent_div_last_amount,
                payDate: s.next_payment_date,
                shares: userShares,
              })
            : null;

          const tooltip = `${s.name || s.ticker}: ${meta.label} just raised ${pctStr} on ${s.recent_div_last_date}${streak}${subline ? ` · ${subline}` : ''}${yearlyBump ? ` · +${fmtUSD(yearlyBump, { digits: 2 })}/yr for you` : ''}`;
          return (
            <div
              key={s.ticker}
              className={`shrink-0 flex items-stretch rounded-md transition-colors ${
                isOwned
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-400/70 dark:border-emerald-600/60 ring-1 ring-emerald-400/30'
                  : 'bg-white/70 dark:bg-zinc-900/70 border border-amber-300/60 dark:border-amber-700/50'
              }`}
              data-owned={isOwned ? 'true' : 'false'}
            >
              <button
                type="button"
                onClick={() => setActiveRaise(s)}
                title={tooltip}
                data-testid={`streak-chip-${s.ticker}`}
                className={`flex flex-col items-start gap-0.5 px-2 py-1 rounded-md transition-colors ${
                  isOwned
                    ? 'hover:bg-emerald-100 dark:hover:bg-emerald-950/60'
                    : 'hover:bg-amber-100 dark:hover:bg-amber-950/60'
                }`}
              >
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold">
                  <span className="text-[11px]" aria-hidden>{meta.emoji}</span>
                  <span className="font-mono font-bold text-foreground">{s.ticker}</span>
                  <span className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-300">
                    <TrendingUp className="w-2.5 h-2.5" />
                    {pctStr}
                  </span>
                  <Flame className="w-2.5 h-2.5 text-orange-500" />
                  {s.has_special_this_year && (
                    <span
                      className="ml-1 px-1 py-[1px] rounded bg-purple-200/70 dark:bg-purple-900/60 text-purple-900 dark:text-purple-100 text-[9px] font-bold tracking-wider uppercase"
                      data-testid={`streak-chip-special-${s.ticker}`}
                      title={`${s.ticker} paid a Massive-classified ${(s.special_types || ['special']).join(' + ')} dividend this year. Don't confuse the yield spike with a regular raise — the base recurring dividend growth is what extends the streak.`}
                    >
                      SPECIAL
                    </span>
                  )}
                  {yearlyBump != null && (
                    <span
                      className="ml-1 px-1 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-[9px] font-mono font-bold"
                      data-testid={`streak-chip-bump-${s.ticker}`}
                    >
                      +{fmtUSD(yearlyBump, { digits: 2 })}/yr
                    </span>
                  )}
                </span>
                {subline && (
                  <span
                    className="text-[10px] font-mono text-muted-foreground/90 leading-none"
                    data-testid={`streak-chip-next-${s.ticker}`}
                  >
                    {subline}
                  </span>
                )}
              </button>
              {ics && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Programmatic anchor + new-tab fallback. The
                    // Emergent preview iframe is sandboxed without
                    // `allow-downloads`, which silently blocks
                    // `<a download>`. Building a temp anchor on click
                    // works in normal browsers; if the iframe sandbox
                    // blocks even that, we fall back to opening the
                    // blob in a new tab so the user still gets the
                    // file. Toast confirms the action either way so
                    // the click never feels dead.
                    try {
                      const a = document.createElement('a');
                      a.href = ics.url;
                      a.download = ics.filename;
                      a.rel = 'noopener';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      toast.success(`${s.ticker} dividend added to calendar download`);
                    } catch (err) {
                      // Last-ditch fallback for very strict sandboxes
                      window.open(ics.url, '_blank', 'noopener,noreferrer');
                      toast.info(`Opened ${s.ticker} .ics in a new tab`);
                    }
                  }}
                  title={`Add ${s.ticker} dividend (${s.next_payment_date}) to your calendar`}
                  data-testid={`streak-chip-cal-${s.ticker}`}
                  className="flex items-center justify-center px-1.5 border-l border-amber-300/40 dark:border-amber-700/30 text-muted-foreground/70 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-100/40 dark:hover:bg-amber-950/40 rounded-r-md transition-colors"
                >
                  <CalendarPlus className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <SwipeHint hint={swipeHint} testId="streak-highlight-swipe-hint" />

      <StreakRaiseDetailModal
        open={!!activeRaise}
        onOpenChange={(o) => { if (!o) setActiveRaise(null); }}
        raise={activeRaise}
        shares={activeRaise ? (ownedShares[activeRaise.ticker] || ownedShares[String(activeRaise.ticker).toUpperCase()] || 0) : 0}
        onOpenFullStock={(tkr) => onStockClick && onStockClick(tkr)}
      />
    </div>
  );
}
